import { useMemo } from "react";
import { IconX } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { IconPill } from "../../../../components/common/icon-pill";
import { Select } from "../../../../components/forms/select";
import { useProductWarranties } from "../../hooks/useProductWarranties";
import { useProductWarrantyTypes } from "../../hooks/useProductWarrantyTypes";
import { useProductWarrantyProviders } from "../../hooks/useProductWarrantyProviders";

export interface Warranty {
  id: number;
  warrantyId: string;
  warrantyTypeId: string;
  warrantyProviderId: string;
}

/** Which of the card's selects the seller must fill (per category policy). */
export interface WarrantyRequirements {
  period?: boolean;
  type?: boolean;
  provider?: boolean;
}

export function WarrantyCard({
  warranty,
  index,
  onRemove,
  onUpdate,
  readOnly = false,
  required = {},
}: {
  warranty: Warranty;
  index: number;
  onRemove: () => void;
  onUpdate: (patch: Partial<Warranty>) => void;
  readOnly?: boolean;
  required?: WarrantyRequirements;
}) {
  const { data: warranties, isLoading: warrantiesLoading } = useProductWarranties();
  const { data: warrantyTypes, isLoading: typesLoading } = useProductWarrantyTypes();
  const { data: warrantyProviders, isLoading: providersLoading } = useProductWarrantyProviders();

  const warrantyOptions = useMemo(
    () => (warranties ?? []).map((w) => ({ value: String(w.id), label: w.name })),
    [warranties],
  );
  const typeOptions = useMemo(
    () => (warrantyTypes ?? []).map((t) => ({ value: String(t.id), label: t.name })),
    [warrantyTypes],
  );
  const providerOptions = useMemo(
    () => (warrantyProviders ?? []).map((p) => ({ value: String(p.id), label: p.name })),
    [warrantyProviders],
  );

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-borderDefault bg-bgLight p-4">
      <div className="flex items-center justify-between">
        <Text variant="bodySmMedium" tone="dark">Warranty {index + 1}</Text>
        {!readOnly && (
          <IconPill
            as="button"
            size={24}
            tone="ghost"
            aria-label="Remove warranty"
            onClick={onRemove}
            className="!bg-transparent !text-textMuted hover:!text-textDark"
          >
            <IconX size={16} />
          </IconPill>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
        <Select
          name={`warranty-period-${warranty.id}`}
          label="Warranty period"
          required={!readOnly && required.period}
          density="compact"
          value={warranty.warrantyId}
          onChange={(e) => onUpdate({ warrantyId: e.target.value })}
          options={warrantyOptions}
          placeholder={warrantiesLoading ? "Loading…" : "Select period"}
          disabled={readOnly || warrantiesLoading}
        />
        <Select
          name={`warranty-type-${warranty.id}`}
          label="Warranty type"
          required={!readOnly && required.type}
          density="compact"
          value={warranty.warrantyTypeId}
          onChange={(e) => onUpdate({ warrantyTypeId: e.target.value })}
          options={typeOptions}
          placeholder={typesLoading ? "Loading…" : "Select type"}
          disabled={readOnly || typesLoading}
        />
        <Select
          name={`warranty-provider-${warranty.id}`}
          label="Warranty provider"
          required={!readOnly && required.provider}
          density="compact"
          value={warranty.warrantyProviderId}
          onChange={(e) => onUpdate({ warrantyProviderId: e.target.value })}
          options={providerOptions}
          placeholder={providersLoading ? "Loading…" : "Select provider"}
          disabled={readOnly || providersLoading}
        />
      </div>
    </div>
  );
}
